import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/lib/analytics";

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
];

const RitualNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: t("nav.silhouettes"), href: "#silhouettes", anchor: true },
    { name: t("nav.myStory"), href: "#my-story", anchor: true },
    { name: t("nav.bridalExperience"), href: "#sustainability", anchor: true },
    { name: t("nav.letters"), href: "/journal", anchor: false },
  ];

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const id = href.replace("#", "");
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + href);
    }
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    trackEvent("language_change", { language: code });
  };

  return (
<nav
  className={`fixed z-50 transition-all duration-500 ${
    scrolled
      ? "top-2 left-3 right-3 md:top-3 md:left-5 md:right-5"
      : "top-4 left-4 right-4 md:top-5 md:left-6 md:right-6"
  }`}
  style={{
    backgroundColor: scrolled
      ? "hsla(40, 10%, 98%, 0.6)"
      : "hsla(40, 10%, 98%, 0.4)",
    backdropFilter: "blur(24px) saturate(110%)",
    WebkitBackdropFilter: "blur(24px) saturate(110%)",
    border: "1px solid hsla(40, 20%, 90%, 0.35)",
    borderRadius: "1rem",
    boxShadow: scrolled
      ? "0 12px 40px hsla(0, 0%, 0%, 0.12), 0 2px 10px hsla(0, 0%, 0%, 0.06), inset 0 1px 0 hsla(0, 0%, 100%, 0.5)"
      : "0 8px 32px hsla(0, 0%, 0%, 0.07), 0 2px 8px hsla(0, 0%, 0%, 0.04), inset 0 1px 0 hsla(0, 0%, 100%, 0.4)",
  }}
>
      {/* Subtle inner highlight */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, transparent 60%)",
          }}
        />
      <div className="relative flex items-center justify-between h-12 md:h-12 px-5 md:px-8">
        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-3 text-foreground"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={t("nav.toggleMenu")}
        >
          <div className="w-5 h-5 relative">
            <span
              className={`absolute block w-5 h-px bg-current transform transition-all duration-300 ${
                isMobileMenuOpen ? "rotate-45 top-2.5" : "top-1.5"
              }`}
            />
            <span
              className={`absolute block w-5 h-px bg-current transform transition-all duration-300 top-2.5 ${
                isMobileMenuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute block w-5 h-px bg-current transform transition-all duration-300 ${
                isMobileMenuOpen ? "-rotate-45 top-2.5" : "top-3.5"
              }`}
            />
          </div>
        </button>

        {/* Desktop nav links */}
        <div className="hidden lg:flex space-x-7">
          {navItems.map((item) =>
            item.anchor ? (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleAnchorClick(e, item.href)}
                className="font-body text-xs tracking-[0.15em] uppercase text-foreground/70 hover:text-foreground transition-colors duration-300"
              >
                {item.name}
              </a>
            ) : (
              <Link
                key={item.href}
                to={item.href}
                className="font-body text-xs tracking-[0.15em] uppercase text-foreground/70 hover:text-foreground transition-colors duration-300"
              >
                {item.name}
              </Link>
            )
          )}
        </div>

        {/* Center logo — protrudes above the bar */}
        <Link
          to="/"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="absolute left-1/2 -translate-x-1/2 inset-y-0 flex flex-col items-center justify-center text-center"
          aria-label={t("nav.home")}
        >
          {/* Primary Brand Name */}
          <span className="font-display text-sm md:text-base tracking-[0.3em] uppercase text-foreground">
            Ritual
          </span>

          {/* Sub-brand / Designer Name */}
          <span className="font-body text-[8px] md:text-[9px] tracking-[0.4em] uppercase text-gold/80 -mt-1">
            By Camila Luna
          </span>
        </Link>

        {/* Right side: CTA + language switcher */}
        <div className="flex items-center gap-3">
          <Link
            to="/consultation"
            onClick={() => { if (location.pathname === "/consultation") window.location.reload(); }}
            className="hidden md:inline-flex font-body text-[10px] tracking-[0.2em] uppercase text-gold border border-gold px-4 py-2 hover:bg-gold hover:text-white transition-all duration-500"
          >
            {t("nav.letsTalk")}
          </Link>
          {/* Language switcher */}
          <div ref={langRef} className="relative">
            <button
              onClick={() => setIsLangOpen((o) => !o)}
              className="flex items-center gap-1 p-2 -m-2 font-body text-[10px] tracking-[0.2em] uppercase text-foreground/70 hover:text-foreground transition-colors duration-300"
            >
              {i18n.language.toUpperCase()}
              <svg
                className={`w-2.5 h-2.5 transition-transform duration-300 ${isLangOpen ? "rotate-180" : ""}`}
                viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5"
              >
                <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {isLangOpen && (
              <div
                className="absolute right-0 top-full mt-3 overflow-hidden rounded-xl"
                style={{
                  backgroundColor: "hsla(40, 10%, 98%, 0.85)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid hsla(40, 20%, 90%, 0.5)",
                  boxShadow: "0 8px 32px hsla(0,0%,0%,0.10), inset 0 1px 0 hsla(0,0%,100%,0.6)",
                }}
              >
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { handleLanguageChange(lang.code); setIsLangOpen(false); }}
                    className={`block w-full px-5 py-2.5 font-body text-[10px] tracking-[0.2em] uppercase text-left transition-colors duration-200 ${
                      i18n.language === lang.code
                        ? "text-gold bg-gold/5"
                        : "text-foreground/60 hover:text-foreground hover:bg-black/[0.03]"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
         <div
            className="lg:hidden border-t rounded-b-2xl overflow-hidden"
           style={{ borderColor: "hsla(40, 20%, 90%, 0.35)" }}
        >
          <div className="px-6 py-8 space-y-6">
            {navItems.map((item) =>
              item.anchor ? (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleAnchorClick(e, item.href)}
                  className="font-display text-2xl text-foreground block"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-display text-2xl text-foreground block"
                >
                  {item.name}
                </Link>
              )
            )}

            {/* Instagram section link */}
            <a
              href="#instagram"
              onClick={(e) => handleAnchorClick(e, "#instagram")}
              className="flex items-center gap-3 pt-2"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.25"
                className="w-4 h-4 text-gold/70 shrink-0"
                aria-hidden="true"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
              </svg>
              <span className="font-display text-2xl text-foreground">
                Instagram
              </span>
            </a>

            <div className="pt-4">
              <Link
                to="/consultation"
                className="inline-flex items-center justify-center w-full px-8 py-4 bg-gold text-white font-body text-xs tracking-[0.2em] uppercase"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  if (location.pathname === "/consultation") window.location.reload();
                }}
              >
                {t("nav.letsTalkMobile")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default RitualNavigation;
